import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";
import { EligibilityFragment, AwardsFragment } from "./fragments";
import { TreeNodeFragment } from "@/Common/fragments/GenericTree";

export async function getCommissionCyclesSpiffs({ locationId, department, commissionCycleId }) {
	const query = gql`
		query getCommissionCyclesSpiffs($locationId: String!, $department: String, $commissionCycleId: String) {
			getCommissionCyclesSpiffs(locationId: $locationId, department: $department, commissionCycleId: $commissionCycleId) {
				_id
				title
				startDate
				status
				endDate
				spiffs {
					_id
					title
					effectiveDateFrom
					effectiveDateTo
					itemsCount
					createdAt
					createdBy {
						_id
						fullName
					}
					lastModifiedOn
					lastModifiedBy {
						_id
						fullName
					}
					items {
						_id
						title
						eligibility {
							...EligibilityFields
						}
						effectiveDateRangeFrom
						effectiveDateRangeTo
						reward
						description
						type
						createdBy {
							_id
							fullName
						}
						awardsCount
						awards {
							_id
							spiffEntry
							spiffDate
							spiffType
							deals {
								stockNumber
								dealNumber
							}
							repairOrders {
								vinNumber
								repairOrderNumber
							}
							reward
							recipient {
								_id
								fullName
							}
						}
						rolloverCounter
						type
						createdAt
						updatedAt
						updatedBy {
							_id
							fullName
						}
						spiffType
						eligibilityDays
						positions
						requiredDealStatus
						transactionType
					}
				}
			}
		}
		${EligibilityFragment}
	`;

	const variables = {
		locationId,
		department,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.getCommissionCyclesSpiffs;
	} catch (err) {
		errorLogger(err, { function: "getCommissionCyclesSpiffs", message: "Error getting commission cycles spiffs", variables });
		return null;
	}
}

export async function getSpiffSetup({ locationId, department, spiffId }) {
	const query = gql`
		query getSpiffList($locationId: String!, $department: String!, $spiffId: String!) {
			getSpiffList(locationId: $locationId, department: $department, spiffId: $spiffId) {
				_id
				title
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					createdAt
					rolloverCounter
					effectiveDateRangeFrom
					effectiveDateRangeTo
					type
					reward
					description
					spiffType
					eligibility {
						...EligibilityFields
					}
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
					createdBy {
						_id
						fullName
					}
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		spiffId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.getSpiffList;
	} catch (err) {
		errorLogger(err, { function: "getSpiffList", message: "Error getting spiff list", variables });
		return null;
	}
}

export async function getUserStockNumbers(locationId, userId, commissionCycleId, filter) {
	const query = gql`
		query getUserStockNumbers($locationId: String!, $userId: String!, $commissionCycleId: String, $filter: CommonFilterInput) {
			getUserStockNumbers(locationId: $locationId, userId: $userId, commissionCycleId: $commissionCycleId, filter: $filter) {
				stockNumber
				dealNumber
			}
		}
	`;

	const variables = { locationId, userId, commissionCycleId, filter };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getUserStockNumbers;
	} catch (err) {
		errorLogger(err, { function: "getUserStockNumbers", message: "Error getting user stock numbers", variables });
		return null;
	}
}
export async function getUserRepairOrderNumbers(locationId, userId, commissionCycleId, filter) {
	const query = gql`
		query getUserRepairOrderNumbers($locationId: String!, $userId: String!, $commissionCycleId: String, $filter: CommonFilterInput) {
			getUserRepairOrderNumbers(locationId: $locationId, userId: $userId, commissionCycleId: $commissionCycleId, filter: $filter) {
				repairOrderNumber
				vinNumber
			}
		}
	`;

	const variables = { locationId, userId, commissionCycleId, filter };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getUserRepairOrderNumbers;
	} catch (err) {
		errorLogger(err, { function: "getUserRepairOrderNumbers", message: "Error getting user repair order numbers", variables });
		return null;
	}
}

export async function getSpiffSetupTree({ locationId, department }) {
	const query = gql`
		query getSpiffSetupTree($locationId: String!, $department: String!) {
			getSpiffSetupTree(locationId: $locationId, department: $department) {
				locationId
				department
				tree {
					...TreeNode
					list {
						...TreeNode
						list {
							...TreeNode
						}
					}
				}
				hints {
					defaultSelectedIds
					expandedIds
					prefetchIds
				}
			}
		}

		${TreeNodeFragment}
	`;

	const variables = {
		locationId,
		department,
	};

	const response = await apiInstance.post("/graphql", { query, variables });
	return response?.data?.data?.getSpiffSetupTree;
}
